// Typed client for the SpeedTrainer.API

import * as authService from './authService';
import { store } from '../store';
import { logout, sessionExpired } from '../store/authSlice';
import type {
  ApiTrackPartDto,
  ApiTrackLinkDto,
  ApiTrackSummaryDto,
  ApiTrackDto,
  CreateTrackBody,
  UpdateTrackBody,
} from '@speed-trainer/shared';

export type {
  ApiTrackPartDto,
  ApiTrackLinkDto,
  ApiTrackSummaryDto,
  ApiTrackDto,
  CreateTrackBody,
  UpdateTrackBody,
};

const API_BASE = (process.env.REACT_APP_API_URL ?? 'http://localhost:5244').replace(/\/$/, '');
const SUBSCRIPTION_KEY = process.env.REACT_APP_SUBSCRIPTION_KEY ?? '';

/** Builds headers with the current Bearer token if one exists. */
function authHeaders(base: Record<string, string> = {}): Record<string, string> {
  const token = authService.getStoredToken();
  const headers: Record<string, string> = { ...base };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (SUBSCRIPTION_KEY) headers['Ocp-Apim-Subscription-Key'] = SUBSCRIPTION_KEY;
  return headers;
}

/**
 * Wraps fetch with:
 *  – proactive token refresh when the token is near/past expiry
 *  – one automatic retry on 401 after a silent refresh
 *  – logout dispatch when the refresh itself fails
 */
async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  // Proactively refresh if the stored token is expired
  if (authService.getStoredToken() && authService.isTokenExpired()) {
    try {
      await authService.silentRefresh();
    } catch {
      // Refresh token itself was rejected — it's expired/invalid, not just the access token.
      store.dispatch(sessionExpired());
      return new Promise<Response>(() => {});
    }
  }

  const doFetch = (): Promise<Response> =>
    fetch(input, {
      ...init,
      headers: authHeaders(init.headers as Record<string, string>),
    });

  const res = await doFetch();

  if (res.status === 401) {
    // Token may have expired mid-flight; try one silent refresh then retry
    try {
      await authService.silentRefresh();
      const retry = await doFetch();
      if (retry.status === 401) {
        // Refresh succeeded but server still rejects – treat as logged out
        store.dispatch(logout());
        return new Promise<Response>(() => {});
      }
      return retry;
    } catch {
      // Refresh token itself was rejected — it's expired/invalid, not just the access token.
      store.dispatch(sessionExpired());
      return new Promise<Response>(() => {});
    }
  }

  return res;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API error ${res.status}${text ? `: ${text}` : ''}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ── API calls ─────────────────────────────────────────────────────────────────

export const trackApiService = {
  getAll(): Promise<ApiTrackDto[]> {
    return authedFetch(`${API_BASE}/api/tracks`).then((r) => handleResponse<ApiTrackDto[]>(r));
  },

  getById(id: string): Promise<ApiTrackDto> {
    return authedFetch(`${API_BASE}/api/tracks/${id}`).then((r) => handleResponse<ApiTrackDto>(r));
  },

  create(body: CreateTrackBody): Promise<ApiTrackDto> {
    return authedFetch(`${API_BASE}/api/tracks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: '', ...body }),
    }).then((r) => handleResponse<ApiTrackDto>(r));
  },

  update(id: string, body: UpdateTrackBody): Promise<ApiTrackDto> {
    return authedFetch(`${API_BASE}/api/tracks/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: '', ...body }),
    }).then((r) => handleResponse<ApiTrackDto>(r));
  },

  delete(id: string): Promise<void> {
    return authedFetch(`${API_BASE}/api/tracks/${id}`, {
      method: 'DELETE',
    }).then((r) => handleResponse<void>(r));
  },
};
